import { useEffect, useState } from "react";
import { authService } from "../services/api";
import { setAuthItem, setAuthJson } from "../utils/authStorage";
import { persistRbacFromLogin } from "../rbac/PermissionsContext";

/**
 * EssProxyPage — opened in a new window by the admin "Access ESS" feature.
 *
 * Flow:
 *  1. index.js already set sessionStorage.essProxySession = 'true' before React mounted.
 *  2. This page extracts the one-time token from ?t=, clears it from the URL,
 *     redeems it with the backend, writes the employee session into sessionStorage
 *     (so the admin's localStorage is never touched), then navigates to /employee/home.
 */
export default function EssProxyPage() {
  const [status, setStatus] = useState("loading"); // "loading" | "error"
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("t");

    // Remove the token from the URL immediately so it isn't visible in history
    window.history.replaceState({}, document.title, "/ess-proxy");

    if (!token) {
      setErrorMsg("Invalid access link. Please try again from the admin panel.");
      setStatus("error");
      return;
    }

    let cancelled = false;

    authService
      .redeemEssProxyToken(token)
      .then((data) => {
        if (cancelled) return;

        // Write session to sessionStorage (isolated from admin's localStorage)
        const displayName =
          data.user?.name ||
          [data.user?.firstName, data.user?.lastName].filter(Boolean).join(" ").trim() ||
          data.employee?.name ||
          data.employee?.empCode ||
          "Employee";
        const profileEmployeeId = data.user?.employeeId ?? data.employee?.id;
        setAuthItem("token", data.token);
        setAuthJson("currentUser", {
          ...data.user,
          name: displayName,
          employeeId: profileEmployeeId ?? data.user?.employeeId,
        });
        setAuthItem("isAuthenticated", "true");
        setAuthItem("userType", "employee");
        setAuthItem("userId", String(data.user?.id ?? ""));
        if (profileEmployeeId != null && profileEmployeeId !== "") {
          setAuthItem("employeeId", String(profileEmployeeId));
        }

        if (data.company) {
          setAuthJson("companyData", data.company);
        }

        // Seed RBAC snapshot into sessionStorage via persistRbacFromLogin
        // (persistRbacFromLogin now uses setAuthItem thanks to PermissionsContext update)
        persistRbacFromLogin({
          user: data.user,
          userType: "employee",
          rbac: data.rbac,
        });

        sessionStorage.setItem("essProxySession", "true");

        // Full navigation to trigger fresh React bootstrap with employee session
        window.location.href = "/employee/home";
      })
      .catch((err) => {
        if (cancelled) return;
        const dataMsg =
          err?.response?.data?.message ||
          (typeof err?.response?.data === "string" ? err.response.data : null);
        const msg =
          typeof err === "string"
            ? err
            : dataMsg ||
              err?.message ||
              err?.error ||
              "Access failed. Please try again.";
        setErrorMsg(msg);
        setStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (status === "error") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6">
        <div className="w-full max-w-md rounded-xl border border-red-200 bg-white p-8 shadow-md text-center">
          <div className="mb-4 flex justify-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(var(--ds-primary))]/10">
              <svg
                className="h-7 w-7 text-[hsl(var(--ds-primary))]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
          </div>
          <h2 className="mb-2 text-lg font-semibold text-gray-900">Access Failed</h2>
          <p className="mb-6 text-sm text-gray-600">{errorMsg}</p>
          <button
            type="button"
            onClick={() => window.close()}
            className="rounded-lg bg-[hsl(var(--ds-primary))] px-5 py-2 text-sm font-medium text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] transition"
          >
            Close Window
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="mb-4 flex justify-center">
          <svg
            className="h-10 w-10 animate-spin text-[hsl(var(--ds-primary))]"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        </div>
        <p className="text-sm font-medium text-gray-600">Opening ESS session…</p>
      </div>
    </div>
  );
}
